import { useEffect, useState } from 'react'

import { useTranslation } from '@/lib/i18n'
import { StoreImage } from '@/components/StoreImage'

export interface VapeOption {
  id: string
  brand: string
  model: string
  label: string
  imageUrl: string | null
}

interface Props {
  vapes: VapeOption[]
  /** Which list this is — picks the wording: the device on a case, the
   *  magnetic tip on the stand. */
  kind?: 'vape' | 'pin' | 'airpods'
  value: string | null
  onChange: (id: string) => void
  /** The parent lifts its z-index while the list is open, so the
   *  menu paints over the cards below the buy row. */
  onOpenChange?: (open: boolean) => void
}

const PICKER_ATTR = 'data-vape-picker'

/**
 * "Made for" — the device a vape case is cut for, from the Vape Case
 * Variants sheet (dropdown variant). A thumbnail-led trigger that
 * takes the material line's slot in the qty row, and a listbox grouped
 * by brand that drops under the row. Pin choices retain their photos;
 * AirPods models use the same dropdown without thumbnails.
 *
 * Renders a fragment on purpose: the trigger is a flex child of the
 * row and the list is positioned against the row itself, so the row
 * carries `relative` and this component adds no box of its own.
 */
export default function VapePicker({ vapes, kind = 'vape', value, onChange, onOpenChange }: Props) {
  const { t } = useTranslation()
  const label = (key: string) => t(`product.${kind}.${key}`)
  const [open, setOpenState] = useState(false)
  const setOpen = (next: boolean) => {
    setOpenState(next)
    onOpenChange?.(next)
  }

  const picked = vapes.find((v) => v.id === value) ?? null
  const brands = Array.from(new Set(vapes.map((v) => v.brand)))
  const showImages = kind !== 'airpods'

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (!(e.target as Element).closest?.(`[${PICKER_ATTR}]`)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('click', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('click', onClick)
      document.removeEventListener('keydown', onKey)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const pick = (id: string) => {
    onChange(id)
    setOpen(false)
  }

  return (
    <>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label('list_aria')}
        onClick={() => setOpen(!open)}
        {...{ [PICKER_ATTR]: '' }}
        className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 rounded-[18px] py-2.5 pl-3 pr-3.5 text-left text-ink outline-none transition-colors duration-150 hover:bg-surface-sunk focus-visible:bg-surface-sunk"
      >
        {showImages && (
          <span
            className={
              'flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-[10px] border border-hairline ' +
              (picked ? 'bg-white' : 'bg-surface-sunk')
            }
          >
            {picked?.imageUrl ? (
              <StoreImage src={picked.imageUrl} alt="" className="size-full object-contain p-[3px]" />
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="square"
                className="text-ink-faint"
                aria-hidden="true"
              >
                <rect x="8.5" y="3.5" width="7" height="17" rx="1.5" />
                <path d="M10.5 7h3M12 12v4" />
              </svg>
            )}
          </span>
        )}
        <span className="flex min-w-0 flex-1 flex-col leading-[1.3]">
          <span className="text-[12px] text-ink">{label('made_for')}</span>
          <span className="truncate text-[15px] font-semibold text-ink">
            {picked ? picked.label : label('pick')}
          </span>
        </span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          width="18"
          height="18"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="square"
          strokeLinejoin="miter"
          aria-hidden="true"
          className={
            'shrink-0 text-ink-muted transition-transform duration-200 ' +
            (open ? 'rotate-180' : '')
          }
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div
          role="listbox"
          aria-label={label('list_aria')}
          {...{ [PICKER_ATTR]: '' }}
          className="absolute -left-px -right-px top-[calc(100%+6px)] z-20 max-h-[min(360px,55vh)] origin-top overflow-y-auto rounded-[14px] border border-hairline bg-surface p-1.5 shadow-[var(--sss-shadow-overlay)] animate-[sssZoomIn_160ms_var(--sss-ease)_both]"
        >
          {brands.map((brand) => (
            <div key={brand}>
              {/* One group needs no heading — the stand's two tips share one. */}
              {brands.length > 1 && (
                <div className="px-2.5 pb-1 pt-2 text-[12px] font-medium text-ink-muted">{brand}</div>
              )}
              {vapes
                .filter((v) => v.brand === brand)
                .map((v) => {
                  const selected = v.id === value
                  return (
                    <button
                      key={v.id}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      onClick={() => pick(v.id)}
                      className={
                        'flex w-full cursor-pointer items-center gap-3 rounded-[10px] py-1.5 pl-1.5 pr-2.5 text-left text-ink transition-colors duration-150 hover:bg-surface-sunk ' +
                        (selected ? 'bg-surface-sunk ' : '') +
                        (showImages ? '' : 'min-h-11')
                      }
                    >
                      {showImages && v.imageUrl && (
                        <span className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-[8px] bg-white">
                          <StoreImage src={v.imageUrl} alt="" className="size-full object-contain p-[3px]" />
                        </span>
                      )}
                      <span className="flex-1 text-[14px] font-medium leading-[1.3]">
                        {v.label}
                      </span>
                      <span
                        className={
                          'flex w-5 items-center justify-center text-signal ' +
                          (selected ? 'opacity-100' : 'opacity-0')
                        }
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          width="16"
                          height="16"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="square"
                          strokeLinejoin="miter"
                          aria-hidden="true"
                        >
                          <path d="M5 12.5l4.5 4.5L19 7" />
                        </svg>
                      </span>
                    </button>
                  )
                })}
            </div>
          ))}
        </div>
      )}
    </>
  )
}
